import { objectSpread2 as _objectSpread2, toConsumableArray as _toConsumableArray, typeof as _typeof } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import isEmpty from 'lodash/isEmpty';
import { ToggleFilter } from '../ToggleFilter.js';
import { DateFilter } from '../DateFilter.js';
import { i18n } from '../../../i18n/translate.js';
import { format } from '../../../utils/dateHelpers.js';
/**
 * Converts a filter array to a key/value object for Foundry API filtering
 * @param {Array} filtersArray An array of filters
 * @param {string} keyField The key field (default is "name")
 * @param {string} valueField  The value field (default is "value")
 */

var filtersArrayToKeyValueObject = function filtersArrayToKeyValueObject(filtersArray) {
  var keyField = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'name';
  var valueField = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'value';
  return filtersArray.reduce(function (acc, filter) {
    acc[filter[keyField]] = filter[valueField];
    return acc;
  }, {});
};
/**
 * Get filter collection to render as category chips
 * @param {Array} filters An array of filters
 */


var getFilterChips = function getFilterChips(filters) {
  return filters.reduce(function (chips, filter) {
    if (!filter.value) {
      return chips;
    }

    if (filter.isTwoStep) {
      filter.value.forEach(function (value) {
        value.values.forEach(function (val) {
          return chips.push(_objectSpread2(_objectSpread2(_objectSpread2({}, filter), value), {}, {
            value: val
          }));
        });
      });
    } else if (Array.isArray(filter.value)) {
      filter.value.forEach(function (value) {
        chips.push(_objectSpread2(_objectSpread2({}, filter), {}, {
          value: value
        }));
      });
    } else if (typeof filter.value === 'boolean') {
      chips.push(filter);
    } else {
      // custom object filters
      var values = Object.values(filter.value);

      if (values.length && values.some(function (val) {
        return !!val;
      })) {
        if (filter.type === DateFilter) {
          formatDateFilterChip(filter);
        }

        chips.push(filter);
      }
    }

    return chips;
  }, []);
};
/**
 * Returns true if the filter is active, or false if it's not, based on the value
 * @param {Object} filter A filter object
 */


var isFilterActive = function isFilterActive(filter) {
  var value = filter.value;
  var isActive = false;

  if (filter.isTwoStep && Array.isArray(value)) {
    isActive = value.flatMap(function (val) {
      return val.values;
    }).length > 0;
  } else if (Array.isArray(value)) {
    isActive = !!value.length && value.some(function (val) {
      return !isEmpty(val);
    });
  } else if (typeof value === 'boolean') {
    isActive = value;
  } else if (value) {
    isActive = !!Object.keys(value).length;
  }

  return isActive;
};

var getInitialFilterValue = function getInitialFilterValue(filter, valueKeys) {
  var options = filter.options,
      valueKey = filter.valueKey;

  if (typeof valueKeys === 'boolean') {
    return valueKeys ? {
      id: valueKeys,
      label: filter.checkedLabel || "Filtering By ".concat(filter.label)
    } : false;
  }

  var hasOptionsAvailable = Array.isArray(valueKeys) && options;
  var valueField = valueKey || 'id';
  return hasOptionsAvailable ? valueKeys.map(function (val) {
    return val[valueField] ? val : options.find(function (opt) {
      return opt[valueField] === val;
    });
  }).filter(function (val) {
    return val;
  }) : valueKeys;
};
/**
 * Get initial filters based on configuration and initial
 * @param {Array} config An array of filters
 * @param {Object} initial An object containing the initial filters
 */


var getInitialFilters = function getInitialFilters(config, initial) {
  return config.map(function (filter) {
    var valueKeys = initial[filter.name];
    var value = [];
    var defaultValue = [];

    if (valueKeys) {
      value = getInitialFilterValue(filter, valueKeys);
      defaultValue = valueKeys;
    }

    if (filter.type === ToggleFilter) {
      var _value;

      value = Array.isArray(value) && !((_value = value) !== null && _value !== void 0 && _value.length) ? false : value;
    }

    return Object.assign({}, filter, {
      value: value,
      defaultValue: defaultValue
    });
  });
};
/**
 * Receives a collection of filters and returns those same filters with cleared values
 * @param {Array} collection An array of filters
 * @param {Function} clearToggleFilter Function to clear toggle filter specifically (optional)
 */


var clearAllFilters = function clearAllFilters(collection) {
  return collection.map(function (filter) {
    if (Array.isArray(filter.value)) {
      filter.value = [];
    } else {
      filter.value = {};

      if (filter.type === ToggleFilter) {
        filter = _objectSpread2(_objectSpread2({}, filter), {}, {
          value: false
        });
      }
    }

    return filter;
  });
};
/**
 * Receives a collection of filters and returns the same collection with a specific filter value cleared
 * @param {Object} filterToRemove The filter to be removed (have it's value cleared out)
 * @param {Array} collection An array of filters
 * @param {Function} clearToggleFilter Function to clear toggle filter specifically (optional)
 */


var removeFilterFromCollection = function removeFilterFromCollection(filterToRemove, collection) {
  return collection.map(function (filter) {
    if (filter.name === filterToRemove.name) {
      if (filter.isTwoStep) {
        var nestedFilter = filter.value.find(function (val) {
          return val.id === filterToRemove.id;
        });
        var otherFilters = filter.value.filter(function (val) {
          return val.id !== filterToRemove.id;
        });
        var nestedValues = nestedFilter.values.filter(function (val) {
          return val.id !== filterToRemove.value.id;
        });
        filter = _objectSpread2(_objectSpread2({}, filter), {}, {
          value: [].concat(_toConsumableArray(otherFilters), [_objectSpread2(_objectSpread2({}, nestedFilter), {}, {
            values: nestedValues
          })])
        });
      } else if (Array.isArray(filter.value)) {
        filter = _objectSpread2(_objectSpread2({}, filter), {}, {
          value: filter.value.filter(function (value) {
            return value !== filterToRemove.value;
          })
        });
      } else {
        filter = _objectSpread2(_objectSpread2({}, filter), {}, {
          value: {}
        });

        if (filter.type === ToggleFilter) {
          filter = _objectSpread2(_objectSpread2({}, filter), {}, {
            value: false
          });
        }
      }
    }

    return filter;
  });
};
/**
 * Returns a filter unique key
 * @param {Object} filter A filter object
 */


var getFilterUniqueKey = function getFilterUniqueKey(filter) {
  if (typeof (filter === null || filter === void 0 ? void 0 : filter.value) === 'string') {
    return "chip_".concat(filter.name, "_").concat(filter.value);
  } else if (_typeof(filter === null || filter === void 0 ? void 0 : filter.value) === 'object') {
    return "chip_".concat(filter.name, "_").concat(filter.value.id || filter.value.value);
  }
};
/**
 * Returns formatted strings for date filter chip
 * @param {Object} filter A filter object
 */


var formatDateFilterChip = function formatDateFilterChip(filter) {
  var _filter$value = filter.value,
      from_date = _filter$value.from_date,
      to_date = _filter$value.to_date;

  if (from_date && to_date) {
    return i18n.t('Between {{ from_date }} and {{ to_date }}', {
      from_date: format(from_date, 'MM/dd/yyyy'),
      to_date: format(to_date, 'MM/dd/yyyy')
    });
  } else if (from_date) {
    return i18n.t('After {{ from_date }}', {
      from_date: format(from_date, 'MM/dd/yyyy')
    });
  } else if (to_date) {
    return i18n.t('Before {{ to_date }}', {
      to_date: format(to_date, 'MM/dd/yyyy')
    });
  }
};

export { clearAllFilters, filtersArrayToKeyValueObject, formatDateFilterChip, getFilterChips, getFilterUniqueKey, getInitialFilters, isFilterActive, removeFilterFromCollection };